import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Plus, Edit, Trash2, DollarSign, Loader2 } from 'lucide-react';
import { payrollApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { cn, formatCurrency } from '../../lib/utils';

type ComponentStatus = 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED';

interface SalaryComponent {
  id: string;
  name: string;
  type: 'earning' | 'deduction';
  is_fixed: boolean;
  default_amount?: number;
  percentage?: number;
  is_taxable: boolean;
  is_active: boolean;
  status?: ComponentStatus;
}

const SalaryStructures: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState<SalaryComponent | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'earning' as 'earning' | 'deduction',
    is_fixed: true,
    default_amount: 0,
    percentage: 0,
    is_taxable: true,
    is_active: true,
  });

  // Fetch salary components (using deductions/benefits as proxy)
  const { data: earnings = [], isLoading: earningsLoading } = useQuery({
    queryKey: ['benefits'],
    queryFn: () => payrollApi.benefits.getAll(),
  });

  const { data: deductions = [], isLoading: deductionsLoading } = useQuery({
    queryKey: ['deductions'],
    queryFn: () => payrollApi.deductions.getAll(),
  });

  const isLoading = earningsLoading || deductionsLoading;

  // Create mutations
  const createBenefitMutation = useMutation({
    mutationFn: payrollApi.benefits.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benefits'] });
      handleCloseDialog();
      toast({ title: 'Component created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const createDeductionMutation = useMutation({
    mutationFn: payrollApi.deductions.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deductions'] });
      handleCloseDialog();
      toast({ title: 'Component created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateBenefitMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => payrollApi.benefits.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benefits'] });
      handleCloseDialog();
      toast({ title: 'Component updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateDeductionMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => payrollApi.deductions.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deductions'] });
      handleCloseDialog();
      toast({ title: 'Component updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Delete mutations
  const deleteBenefitMutation = useMutation({
    mutationFn: (id: string) => payrollApi.benefits.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benefits'] });
      toast({ title: 'Earning deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteDeductionMutation = useMutation({
    mutationFn: (id: string) => payrollApi.deductions.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deductions'] });
      toast({ title: 'Deduction deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Status change mutations
  const statusChangeBenefitMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ComponentStatus }) =>
      payrollApi.benefits.update(id, { status, is_active: status === 'ACTIVE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benefits'] });
      toast({ title: 'Status updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const statusChangeDeductionMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ComponentStatus }) =>
      payrollApi.deductions.update(id, { status, is_active: status === 'ACTIVE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deductions'] });
      toast({ title: 'Status updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleDeleteEarning = (earning: any) => {
    if (window.confirm(`Are you sure you want to delete "${earning.name}"?`)) {
      deleteBenefitMutation.mutate(earning.id);
    }
  };

  const handleDeleteDeduction = (deduction: any) => {
    if (window.confirm(`Are you sure you want to delete "${deduction.name}"?`)) {
      deleteDeductionMutation.mutate(deduction.id);
    }
  };

  const handleStatusChangeEarning = (id: string, status: ComponentStatus) => {
    statusChangeBenefitMutation.mutate({ id, status });
  };

  const handleStatusChangeDeduction = (id: string, status: ComponentStatus) => {
    statusChangeDeductionMutation.mutate({ id, status });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'default';
      case 'INACTIVE': return 'secondary';
      case 'DISCONTINUED': return 'destructive';
      default: return 'default';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/50';
      case 'INACTIVE':
        return 'bg-slate-50 text-slate-700 border-slate-200/50 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700/50';
      case 'DISCONTINUED':
        return 'bg-rose-50 text-rose-700 border-rose-200/50 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800/50';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200/50 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700/50';
    }
  };

  const handleCreate = () => {
    if (!formData.name.trim()) {
      toast({ title: 'Error', description: 'Please enter component name', variant: 'destructive' });
      return;
    }

    if (formData.type === 'earning') {
      const benefitData = {
        name: formData.name,
        benefit_type: 'SALARY_COMPONENT',
        description: '',
        employee_cost: formData.is_fixed ? (formData.default_amount || 0) : 0,
        employer_cost: 0,
        is_percentage: !formData.is_fixed,
        percentage: !formData.is_fixed ? (formData.percentage || 0) : 0,
        is_active: formData.is_active,
      };
      createBenefitMutation.mutate(benefitData);
    } else {
      const deductionData = {
        name: formData.name,
        deduction_type: 'SALARY_COMPONENT',
        amount: formData.is_fixed ? (formData.default_amount || 0) : 0,
        is_percentage: !formData.is_fixed,
        percentage: !formData.is_fixed ? (formData.percentage || 0) : 0,
        is_active: formData.is_active,
      };
      createDeductionMutation.mutate(deductionData);
    }
  };

  const handleUpdate = () => {
    if (!editingComponent || !formData.name.trim()) {
      return;
    }

    if (formData.type === 'earning') {
      // For benefits/earnings, use employee_cost field
      const data = {
        name: formData.name,
        employee_cost: formData.is_fixed ? (formData.default_amount || 0) : 0,
        employer_cost: 0,
        is_percentage: !formData.is_fixed,
        percentage: !formData.is_fixed ? (formData.percentage || 0) : 0,
        is_active: formData.is_active,
      };
      updateBenefitMutation.mutate({ id: editingComponent.id, data });
    } else {
      // For deductions, use amount field
      const data = {
        name: formData.name,
        amount: formData.is_fixed ? (formData.default_amount || 0) : 0,
        is_percentage: !formData.is_fixed,
        percentage: !formData.is_fixed ? (formData.percentage || 0) : 0,
        is_active: formData.is_active,
      };
      updateDeductionMutation.mutate({ id: editingComponent.id, data });
    }
  };

  const handleEdit = (component: any, type: 'earning' | 'deduction') => {
    setEditingComponent({ ...component, type });
    setFormData({
      name: component.name,
      type,
      is_fixed: !component.is_percentage,
      default_amount: component.amount || component.employee_cost || component.employer_cost || 0,
      percentage: component.percentage || 0,
      is_taxable: true,
      is_active: component.is_active !== false,
    });
  };

  const handleCloseDialog = () => {
    setIsCreateOpen(false);
    setEditingComponent(null);
    setFormData({
      name: '',
      type: 'earning',
      is_fixed: true,
      default_amount: 0,
      percentage: 0,
      is_taxable: true,
      is_active: true,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 font-bold text-slate-400">Loading salary structures...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Define salary components, earnings, and deductions</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
          <Button 
            onClick={() => setIsCreateOpen(true)} 
          >
            <Plus className="h-4 w-4 mr-1.5" />
            <span>Add Component</span>
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Earnings</p>
                <p className="text-3xl font-bold text-slate-800 dark:text-white leading-none mt-1">{earnings.length}</p>
              </div>
              <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 shadow-sm shrink-0">
                <DollarSign className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Deductions</p>
                <p className="text-3xl font-bold text-slate-800 dark:text-white leading-none mt-1">{deductions.length}</p>
              </div>
              <div className="p-3 rounded-2xl bg-rose-50 dark:bg-rose-900/20 text-rose-500 shadow-sm shrink-0">
                <DollarSign className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Total Components</p>
                <p className="text-3xl font-bold text-slate-800 dark:text-white leading-none mt-1">{earnings.length + deductions.length}</p>
              </div>
              <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-505 shadow-sm shrink-0">
                <DollarSign className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Earnings */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 shrink-0" />
            <span>Earnings</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Component Name</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Type</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Amount</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 text-right px-8">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {earnings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-20 px-8 text-slate-400 font-semibold text-xs">
                    No earnings configured
                  </TableCell>
                </TableRow>
              ) : (
                earnings.map((earning: any) => (
                  <TableRow key={earning.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-850/50">
                    <TableCell className="font-bold text-xs text-slate-700 dark:text-slate-300 px-8 py-4">{earning.name}</TableCell>
                    <TableCell className="py-4">
                      <Badge className="px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none bg-blue-50 text-blue-700 border-blue-200/60 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/50" variant="outline">
                        {earning.is_percentage ? 'Percentage' : 'Fixed'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-black text-slate-850 dark:text-slate-150 py-4">
                      {earning.is_percentage
                        ? `${earning.percentage || 0}%`
                        : formatCurrency(earning.amount || earning.employee_cost || earning.employer_cost || 0)}
                    </TableCell>
                    <TableCell className="py-4">
                      <Select
                        value={earning.status || 'ACTIVE'}
                        onValueChange={(value) => handleStatusChangeEarning(earning.id, value as ComponentStatus)}
                      >
                        <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-9 w-[130px] rounded-xl shadow-sm pl-3 pr-2">
                          <Badge className={cn("px-2 py-0.5 rounded-lg font-black text-[9px] tracking-wide border shadow-none", getStatusBadgeClass(earning.status || 'ACTIVE'))} variant="outline">
                            {earning.status || 'ACTIVE'}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent className="border-slate-100 dark:border-slate-800">
                          <SelectItem value="ACTIVE" className="text-xs font-bold">Active</SelectItem>
                          <SelectItem value="INACTIVE" className="text-xs font-bold">Inactive</SelectItem>
                          <SelectItem value="DISCONTINUED" className="text-xs font-bold">Discontinued</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="px-8 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(earning, 'earning')}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteEarning(earning)}
                          disabled={deleteBenefitMutation.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Deductions */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-500 shrink-0" />
            <span>Deductions</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Component Name</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Type</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Amount</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 text-right px-8">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deductions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-20 px-8 text-slate-400 font-semibold text-xs">
                    No deductions configured
                  </TableCell>
                </TableRow>
              ) : (
                deductions.map((deduction: any) => (
                  <TableRow key={deduction.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-850/50">
                    <TableCell className="font-bold text-xs text-slate-700 dark:text-slate-300 px-8 py-4">{deduction.name}</TableCell>
                    <TableCell className="py-4">
                      <Badge className="px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none bg-blue-50 text-blue-700 border-blue-200/60 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/50" variant="outline">
                        {deduction.is_percentage ? 'Percentage' : 'Fixed'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-black text-slate-850 dark:text-slate-155 py-4">
                      {deduction.is_percentage
                        ? `${deduction.percentage || 0}%`
                        : formatCurrency(deduction.amount || deduction.employee_cost || 0)}
                    </TableCell>
                    <TableCell className="py-4">
                      <Select
                        value={deduction.status || 'ACTIVE'}
                        onValueChange={(value) => handleStatusChangeDeduction(deduction.id, value as ComponentStatus)}
                      >
                        <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-9 w-[130px] rounded-xl shadow-sm pl-3 pr-2">
                          <Badge className={cn("px-2 py-0.5 rounded-lg font-black text-[9px] tracking-wide border shadow-none", getStatusBadgeClass(deduction.status || 'ACTIVE'))} variant="outline">
                            {deduction.status || 'ACTIVE'}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent className="border-slate-100 dark:border-slate-800">
                          <SelectItem value="ACTIVE" className="text-xs font-bold">Active</SelectItem>
                          <SelectItem value="INACTIVE" className="text-xs font-bold">Inactive</SelectItem>
                          <SelectItem value="DISCONTINUED" className="text-xs font-bold">Discontinued</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="px-8 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(deduction, 'deduction')}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteDeduction(deduction)}
                          disabled={deleteDeductionMutation.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateOpen || !!editingComponent} onOpenChange={handleCloseDialog}>
        <DialogContent className="w-[min(96vw,450px)] max-w-[450px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-500" />
              <span>{editingComponent ? 'Edit' : 'Add'} Salary Component</span>
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Configure a salary component for your payroll structure
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-bold text-slate-400 tracking-wider">Component Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Housing Allowance, Tax Deduction"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="type" className="text-xs font-bold text-slate-400 tracking-wider">Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value as 'earning' | 'deduction' })}
                disabled={!!editingComponent}
              >
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="border-slate-100 dark:border-slate-800">
                  <SelectItem value="earning" className="text-sm font-bold">Earning</SelectItem>
                  <SelectItem value="deduction" className="text-sm font-bold">Deduction</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="calculation" className="text-xs font-bold text-slate-400 tracking-wider">Calculation Method</Label>
              <Select
                value={formData.is_fixed ? 'fixed' : 'percentage'}
                onValueChange={(value) => setFormData({ ...formData, is_fixed: value === 'fixed' })}
              >
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent className="border-slate-100 dark:border-slate-800">
                  <SelectItem value="fixed" className="text-sm font-bold">Fixed Amount</SelectItem>
                  <SelectItem value="percentage" className="text-sm font-bold">Percentage</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.is_fixed ? (
              <div className="space-y-1.5">
                <Label htmlFor="default_amount" className="text-xs font-bold text-slate-400 tracking-wider">Amount *</Label>
                <Input
                  id="default_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.default_amount || ''}
                  onChange={(e) => setFormData({ ...formData, default_amount: parseFloat(e.target.value) || 0 })}
                  placeholder="Enter amount (e.g., 500.00)"
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
                <p className="text-[10px] font-bold text-slate-400 mt-1">Enter the fixed amount for this component</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="percentage" className="text-xs font-bold text-slate-400 tracking-wider">Percentage *</Label>
                <Input
                  id="percentage"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.percentage || ''}
                  onChange={(e) => setFormData({ ...formData, percentage: parseFloat(e.target.value) || 0 })}
                  placeholder="Enter percentage (e.g., 10)"
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
                <p className="text-[10px] font-bold text-slate-400 mt-1">Percentage of base salary (0-100)</p>
              </div>
            )}
          </div>
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={editingComponent ? handleUpdate : handleCreate}
              disabled={createBenefitMutation.isPending || createDeductionMutation.isPending || updateBenefitMutation.isPending || updateDeductionMutation.isPending}
            >
              {editingComponent ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalaryStructures;